import { Component, EventEmitter, HostListener, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { SpaceDraftError, SpaceDraftSummary, SpaceDraftsService } from '../../services/space-drafts.service';
import { DialogService } from '../../services/dialog.service';
import { NotificationService } from '../../services/notification.service';
import { EsDatePipe } from '../../shared/es-date.pipe';

const STEP_LABELS: Record<string, string> = {
  info: 'Información y fotos',
  precio: 'Capacidad y precio',
  ubicacion: 'Ubicación',
  horarios: 'Horarios',
};

/**
 * Borradores de espacios guardados por el negocio (en el servidor): se ven
 * desde cualquier dispositivo y cualquier persona con permiso puede
 * continuarlos o eliminarlos.
 */
@Component({
  selector: 'app-space-drafts-modal',
  standalone: true,
  imports: [EsDatePipe, CommonModule],
  template: `
    <div class="dm-overlay" (mousedown)="onOverlay($event)">
      <section class="dm-modal" role="dialog" aria-modal="true" aria-labelledby="dm-title">
        <header>
          <div>
            <h2 id="dm-title"><i class="bi bi-journal-text" aria-hidden="true"></i> Borradores guardados</h2>
            <p>Compartidos con tu equipo: cualquiera puede continuarlos desde otro dispositivo.</p>
          </div>
          <button type="button" class="dm-icon" (click)="closed.emit()" aria-label="Cerrar"><i class="bi bi-x-lg"></i></button>
        </header>

        <p class="dm-state" *ngIf="loading()"><i class="bi bi-arrow-repeat" aria-hidden="true"></i> Cargando borradores…</p>
        <p class="dm-state dm-error" *ngIf="error()">{{ error() }} <button type="button" class="dm-link" (click)="load()">Reintentar</button></p>

        <div class="dm-empty" *ngIf="!loading() && !error() && !drafts().length">
          <i class="bi bi-journal-plus" aria-hidden="true"></i>
          <strong>No hay borradores</strong>
          <span>Mientras creas un espacio, usa «Guardar borrador» para continuarlo después aquí.</span>
        </div>

        <ul class="dm-list" *ngIf="!loading() && drafts().length">
          <li *ngFor="let draft of drafts(); trackBy: trackDraft" [class.current]="draft.draft_id === currentDraftId">
            <span class="dm-cover" aria-hidden="true">
              <img *ngIf="draft.cover_url; else noCover" [src]="draft.cover_url" alt="" loading="lazy" />
              <ng-template #noCover><i class="bi bi-image"></i></ng-template>
              <b *ngIf="draft.image_count">{{ draft.image_count }}</b>
            </span>
            <div class="dm-main">
              <strong>{{ draft.title || 'Espacio sin nombre' }}</strong>
              <span class="dm-meta">
                <span><i class="bi bi-clock-history" aria-hidden="true"></i> {{ draft.updated_at | esDate:'d MMM, h:mm a' }}{{ draft.updated_by_name ? ' · ' + draft.updated_by_name : '' }}</span>
                <span><i class="bi bi-signpost-split" aria-hidden="true"></i> {{ stepLabel(draft.current_step) }}</span>
              </span>
              <span class="dm-progress" [attr.aria-label]="'Avance ' + draft.progress + '%'"><span [style.width.%]="draft.progress"></span></span>
            </div>
            <span class="dm-pct">{{ draft.progress }}%</span>
            <div class="dm-actions">
              <button type="button" class="dm-btn primary" (click)="select.emit(draft)" [disabled]="busyId() === draft.draft_id">
                {{ draft.draft_id === currentDraftId ? 'Abierto' : 'Continuar' }}
              </button>
              <button type="button" class="dm-icon danger" (click)="remove(draft)" [disabled]="busyId() === draft.draft_id" [attr.aria-label]="'Eliminar borrador ' + (draft.title || 'sin nombre')" title="Eliminar"><i class="bi bi-trash3"></i></button>
            </div>
          </li>
        </ul>
        <p class="dm-foot" *ngIf="drafts().length">{{ drafts().length }} de {{ max() }} borradores · las fotos se guardan con cada borrador.</p>
      </section>
    </div>
  `,
  styles: [`
    .dm-overlay { position: fixed; inset: 0; z-index: 3200; display: grid; place-items: center; padding: 1rem; background: var(--ts-bg-navy-950-a72-2); }
    .dm-modal { width: min(640px, 100%); max-height: calc(100dvh - 2rem); overflow-y: auto; box-sizing: border-box; display: grid; gap: .8rem; padding: 1.2rem; border-radius: 16px; border: 1px solid var(--ts-border-strong); background: var(--ts-bg-elevated); color: var(--ts-text); box-shadow: 0 26px 72px var(--ts-shadow-gray-1000-a42); }
    header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; }
    h2 { display: flex; align-items: center; gap: .5rem; margin: 0; font-size: 1.08rem; }
    h2 i { color: var(--ts-text-blue-250-3); }
    header p { margin: .25rem 0 0; color: var(--ts-text-muted); font-size: .8rem; }
    .dm-state { margin: 0; color: var(--ts-text-muted); font-size: .86rem; }
    .dm-error { color: var(--ts-danger-text, #ff8a8a); }
    .dm-link { padding: 0; border: 0; background: none; color: var(--ts-text-blue-250-3); font: inherit; font-weight: 750; cursor: pointer; }
    .dm-empty { display: grid; justify-items: center; gap: .3rem; padding: 1.6rem 1rem; border: 1px dashed var(--ts-border-strong); border-radius: 12px; text-align: center; }
    .dm-empty i { color: var(--ts-text-blue-250-3); font-size: 1.6rem; }
    .dm-empty span { max-width: 44ch; color: var(--ts-text-muted); font-size: .82rem; }
    .dm-list { list-style: none; margin: 0; padding: 0; display: grid; gap: .5rem; }
    .dm-cover { position: relative; width: 64px; height: 48px; flex: none; display: grid; place-items: center; overflow: hidden; border-radius: 9px; color: var(--ts-text-muted); background: var(--ts-bg-elevated); border: 1px solid var(--ts-border); }
    .dm-cover img { width: 100%; height: 100%; object-fit: cover; }
    .dm-cover b { position: absolute; right: 3px; bottom: 3px; min-width: 16px; padding: 0 4px; border-radius: 999px; color: var(--ts-text-gray-0); background: var(--ts-bg-navy-950-a75); font-size: .62rem; text-align: center; }
    .dm-list li { display: grid; grid-template-columns: auto minmax(0, 1fr) auto auto; align-items: center; gap: .8rem; padding: .7rem .8rem; border: 1px solid var(--ts-border); border-radius: 12px; background: var(--ts-surface); }
    .dm-list li.current { border-color: var(--ts-primary); }
    .dm-main { display: grid; gap: .25rem; min-width: 0; }
    .dm-main strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: .92rem; }
    .dm-meta { display: flex; flex-wrap: wrap; gap: .2rem .8rem; color: var(--ts-text-muted); font-size: .74rem; }
    .dm-meta i { margin-right: .2rem; }
    .dm-progress { height: 4px; border-radius: 999px; background: var(--ts-border); overflow: hidden; }
    .dm-progress span { display: block; height: 100%; background: linear-gradient(90deg, var(--ts-bg-blue-500), var(--ts-bg-sky-350)); }
    .dm-pct { color: var(--ts-text-soft); font-size: .78rem; font-weight: 800; font-variant-numeric: tabular-nums; }
    .dm-actions { display: flex; gap: .35rem; }
    .dm-btn, .dm-icon { display: inline-flex; align-items: center; justify-content: center; border-radius: 9px; border: 1px solid var(--ts-border-strong); color: var(--ts-text); background: var(--ts-surface); font: inherit; cursor: pointer; }
    .dm-btn { min-height: 34px; padding: 0 .8rem; font-size: .8rem; font-weight: 750; }
    .dm-btn.primary { border-color: transparent; color: var(--ts-text-on-accent); background: linear-gradient(135deg, var(--ts-bg-blue-500), var(--ts-bg-sky-450)); }
    .dm-icon { width: 34px; height: 34px; flex: none; }
    .dm-icon.danger:hover:not(:disabled) { border-color: var(--ts-danger); color: var(--ts-danger-text); }
    .dm-btn:disabled, .dm-icon:disabled { opacity: .5; cursor: not-allowed; }
    .dm-foot { margin: 0; color: var(--ts-text-muted); font-size: .74rem; }
    @media (max-width: 520px) {
      .dm-list li { grid-template-columns: auto minmax(0, 1fr) auto; }
      .dm-pct { display: none; }
    }
  `],
})
export class SpaceDraftsModalComponent implements OnInit {
  @Input({ required: true }) businessId!: number;
  @Input() currentDraftId: number | null = null;
  @Output() select = new EventEmitter<SpaceDraftSummary>();
  @Output() closed = new EventEmitter<void>();
  /** El listado cambió (para contadores externos). */
  @Output() countChange = new EventEmitter<number>();

  private readonly drafts$ = inject(SpaceDraftsService);
  private readonly dialogs = inject(DialogService);
  private readonly notifications = inject(NotificationService);

  readonly drafts = signal<SpaceDraftSummary[]>([]);
  readonly max = signal(30);
  readonly loading = signal(false);
  readonly error = signal('');
  readonly busyId = signal<number | null>(null);

  ngOnInit(): void { void this.load(); }

  async load(): Promise<void> {
    this.loading.set(true);
    this.error.set('');
    try {
      const response = await firstValueFrom(this.drafts$.list(this.businessId));
      this.drafts.set(response.drafts);
      this.max.set(response.max);
      this.countChange.emit(response.drafts.length);
    } catch (error) {
      this.error.set((error as SpaceDraftError)?.message || 'No se pudieron cargar los borradores.');
    } finally {
      this.loading.set(false);
    }
  }

  stepLabel(step: string): string { return STEP_LABELS[step] || 'Información y fotos'; }

  async remove(draft: SpaceDraftSummary): Promise<void> {
    const ok = await this.dialogs.confirm({
      tone: 'danger',
      title: 'Eliminar borrador',
      message: `«${draft.title || 'Espacio sin nombre'}» se eliminará para todo tu equipo${draft.image_count ? ` y sus ${draft.image_count} foto(s) se borrarán definitivamente` : ''}. No afecta a ningún espacio publicado.`,
      confirmText: 'Eliminar',
    });
    if (!ok) return;
    this.busyId.set(draft.draft_id);
    try {
      await firstValueFrom(this.drafts$.remove(draft.draft_id));
      this.drafts.update(list => list.filter(item => item.draft_id !== draft.draft_id));
      this.countChange.emit(this.drafts().length);
      this.notifications.success('Borrador eliminado.');
    } catch (error) {
      const e = error as SpaceDraftError;
      if (e?.code === 'DRAFT_NOT_FOUND') {
        this.drafts.update(list => list.filter(item => item.draft_id !== draft.draft_id));
        this.countChange.emit(this.drafts().length);
      } else {
        this.notifications.error(e?.message || 'No se pudo eliminar el borrador.');
      }
    } finally {
      this.busyId.set(null);
    }
  }

  onOverlay(event: MouseEvent): void { if (event.target === event.currentTarget) this.closed.emit(); }

  @HostListener('document:keydown.escape')
  onEscape(): void { if (!document.querySelector('.dialog-overlay')) this.closed.emit(); }

  trackDraft(_index: number, draft: SpaceDraftSummary): number { return draft.draft_id; }
}
